import { useState } from 'react'
import {
  ArrowRight, BarChart3, CalendarDays, Check, ChevronDown, CircleDollarSign,
  Clock3, CreditCard, Menu, MessageSquare, Sparkles, Users, WandSparkles, X,
} from 'lucide-react'

const APP_URL = 'https://app.joinyouni.com'

const learnerTypes = ['Independent tutors', 'Music schools', 'Language academies', 'Test prep teams', 'Learning centers', 'Coaches']

const schedule = [
  { time: '9:00', name: 'Maya Chen', subject: 'Algebra II', tone: 'gold' },
  { time: '11:30', name: 'Theo Martin', subject: 'Piano · Level 3', tone: 'blue' },
  { time: '2:00', name: 'Ava Johnson', subject: 'SAT Reading', tone: 'lilac' },
  { time: '4:30', name: 'Noah Williams', subject: 'Spanish A2', tone: 'green' },
]

const invoices = [
  { name: 'Maya Chen', detail: '4-lesson package', amount: '$320', status: 'Paid' },
  { name: 'Theo Martin', detail: 'Monthly membership', amount: '$240', status: 'Paid' },
  { name: 'Ava Johnson', detail: 'SAT intensive', amount: '$480', status: 'Due Fri' },
]

function LogoGlyph({ large = false }: { large?: boolean }) {
  return (
    <span className={`logo-glyph${large ? ' logo-glyph-large' : ''}`} aria-hidden="true">
      <i className="glyph-prong glyph-left" />
      <i className="glyph-prong glyph-upper-left" />
      <i className="glyph-prong glyph-up" />
      <i className="glyph-prong glyph-upper-right" />
      <i className="glyph-prong glyph-right" />
      <i className="glyph-center" />
    </span>
  )
}

function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <span className="brand" aria-label="Youni home">
      <span className="brand-mark"><LogoGlyph /></span>
      {!compact && <span className="brand-name">youni</span>}
    </span>
  )
}

function ProductDashboard() {
  return (
    <div className="product-stage" aria-label="Youni product preview">
      <div className="app-shell">
        <aside className="app-sidebar">
          <BrandMark compact />
          <div className="sidebar-links">
            <span className="active"><BarChart3 size={15} /> Overview</span>
            <span><CalendarDays size={15} /> Schedule</span>
            <span><Users size={15} /> People</span>
            <span><CreditCard size={15} /> Billing</span>
          </div>
          <div className="sidebar-profile">
            <span>TM</span>
            <div><strong>Tal's Studio</strong><small>Admin workspace</small></div>
          </div>
        </aside>

        <div className="app-main">
          <div className="app-topbar">
            <div><small>Monday, July 20</small><h3>Good morning, Tal</h3></div>
            <button type="button"><Sparkles size={14} /> Ask Youni</button>
          </div>

          <div className="metric-grid">
            <div><span>Today's lessons</span><strong>8</strong><small>2 more than last Monday</small></div>
            <div><span>Monthly revenue</span><strong>$12.4k</strong><small className="positive">↑ 18.2% this month</small></div>
            <div><span>Active learners</span><strong>64</strong><small>4 joined this month</small></div>
          </div>

          <div className="dashboard-grid">
            <div className="schedule-card">
              <div className="card-heading"><div><small>Today</small><h4>Your schedule</h4></div><button type="button">View calendar</button></div>
              <div className="lesson-list">
                {schedule.map((lesson) => (
                  <div className="lesson-row" key={lesson.name}>
                    <time>{lesson.time}</time><span className={`lesson-dot ${lesson.tone}`} />
                    <div><strong>{lesson.name}</strong><small>{lesson.subject}</small></div>
                    <span className="lesson-status">Confirmed</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="insight-card">
              <span className="insight-icon"><WandSparkles size={17} /></span><small>Youni insight</small>
              <h4>You have 3 open spots this Thursday.</h4>
              <p>Offer these times to learners on your waitlist?</p>
              <button type="button">Draft messages <ArrowRight size={13} /></button>
              <div className="mini-avatars"><span>MC</span><span>JT</span><span>+5</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SchedulePreview() {
  return (
    <div className="feature-visual schedule-visual">
      <div className="week-header"><button type="button">‹</button><strong>July 20 to 24</strong><button type="button">›</button></div>
      <div className="week-grid">
        {['Mon 20', 'Tue 21', 'Wed 22', 'Thu 23', 'Fri 24'].map((day, index) => (
          <div className="day-column" key={day}>
            <span>{day}</span>
            {index === 0 && <div className="calendar-event gold"><small>9:00</small><strong>Algebra II</strong><em>Maya</em></div>}
            {index === 1 && <div className="calendar-event blue"><small>11:30</small><strong>Piano</strong><em>Theo</em></div>}
            {index === 2 && <div className="calendar-event lilac"><small>2:00</small><strong>SAT Reading</strong><em>Ava</em></div>}
            {index === 3 && <div className="calendar-event green"><small>4:30</small><strong>Spanish</strong><em>Noah</em></div>}
            {index === 4 && <div className="available-slot"><span>+</span> Open</div>}
          </div>
        ))}
      </div>
      <div className="automation-toast"><Sparkles size={15} /><div><strong>Conflict handled</strong><small>Ava's lesson was moved and everyone was notified.</small></div><Check size={15} /></div>
    </div>
  )
}

function BillingPreview() {
  return (
    <div className="billing-window">
      <div className="billing-topline"><div><small>Revenue this month</small><strong>$12,480</strong><span>↑ 18.2%</span></div><button type="button">Create invoice</button></div>
      <div className="revenue-chart" aria-hidden="true">
        {[38, 51, 44, 63, 56, 76, 88, 74, 94].map((height, index) => <span key={index} style={{ height: `${height}%` }} />)}
      </div>
      <div className="invoice-table">
        <div className="invoice-head"><span>Customer</span><span>Amount</span><span>Status</span></div>
        {invoices.map((invoice) => (
          <div className="invoice-row" key={invoice.name}>
            <div><span>{invoice.name.split(' ').map((part) => part[0]).join('')}</span><p><strong>{invoice.name}</strong><small>{invoice.detail}</small></p></div>
            <strong>{invoice.amount}</strong><em className={invoice.status === 'Paid' ? 'paid' : 'due'}>{invoice.status}</em>
          </div>
        ))}
      </div>
    </div>
  )
}

function StudentPreview() {
  return (
    <div className="student-window">
      <div className="student-profile"><span className="profile-avatar">MC</span><div><h4>Maya Chen</h4><p>Algebra II · 8 months</p></div><button type="button">Message</button></div>
      <div className="student-stats"><div><small>Lessons</small><strong>24</strong></div><div><small>Attendance</small><strong>96%</strong></div><div><small>Balance</small><strong>$0</strong></div></div>
      <div className="progress-block"><div><span>Learning goal</span><strong>Quadratic equations</strong></div><div className="progress-track"><span /></div><small>7 of 10 milestones complete</small></div>
      <div className="activity-feed">
        <h5>Recent activity</h5>
        <div><span><Check size={13} /></span><p><strong>Lesson completed</strong><small>Factoring polynomials · Today</small></p></div>
        <div><span><MessageSquare size={13} /></span><p><strong>Progress note shared</strong><small>Sent to Priya Chen · Jul 16</small></p></div>
        <div><span><CreditCard size={13} /></span><p><strong>Package renewed</strong><small>4 lessons · Jul 12</small></p></div>
      </div>
    </div>
  )
}

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  return (
    <div className="site-shell">
      <header className="site-header">
        <a href="#top" className="logo-link"><BrandMark /></a>
        <nav className="desktop-nav" aria-label="Main navigation">
          <a href="#product">Product <ChevronDown size={14} /></a><a href="#solutions">Solutions <ChevronDown size={14} /></a>
          <a href="#why-youni">Why Youni</a><a href="#pricing">Pricing</a>
        </nav>
        <div className="nav-actions"><a className="login-link" href={APP_URL}>Log in</a><a className="button button-dark button-small" href={APP_URL}>Get started</a></div>
        <button className="menu-button" type="button" aria-label="Toggle navigation" aria-expanded={isMenuOpen} onClick={() => setIsMenuOpen((open) => !open)}>{isMenuOpen ? <X /> : <Menu />}</button>
        {isMenuOpen && <nav className="mobile-nav" aria-label="Mobile navigation"><a href="#product" onClick={() => setIsMenuOpen(false)}>Product</a><a href="#solutions" onClick={() => setIsMenuOpen(false)}>Solutions</a><a href="#why-youni" onClick={() => setIsMenuOpen(false)}>Why Youni</a><a href="#pricing" onClick={() => setIsMenuOpen(false)}>Pricing</a><a href={APP_URL} onClick={() => setIsMenuOpen(false)}>Log in</a><a className="button button-dark" href={APP_URL} onClick={() => setIsMenuOpen(false)}>Get started</a></nav>}
      </header>

      <main>
        <section className="hero grid-surface" id="top">
          <div className="hero-copy">
            <a className="announcement" href={APP_URL}><span>Live</span> Made for tutors, schools, and academies <ArrowRight size={13} /></a>
            <h1>Make teaching your <span>only priority</span></h1>
            <p>Youni handles scheduling, payments, learner records, and day-to-day admin, all in one place.</p>
            <div className="hero-actions"><a className="button button-dark" href={APP_URL}>Get started <ArrowRight size={16} /></a><a className="button button-light" href="#product">See how it works</a></div>
          </div>
          <div className="product-switcher" aria-label="Product areas"><span><CalendarDays size={14} /> Smart scheduling</span><span><CreditCard size={14} /> Effortless billing</span><span><Users size={14} /> Learner CRM</span></div>
          <ProductDashboard />
        </section>

        <section className="audience-strip" aria-label="Who Youni is for"><p>For every kind of teaching business</p><div>{learnerTypes.map((type) => <span key={type}>{type}</span>)}</div></section>

        <section className="manifesto" id="why-youni">
          <div className="orbit-card orbit-left"><Clock3 size={16} /><span><strong>12 hours saved</strong><small>This week</small></span></div>
          <div className="manifesto-copy"><p>Teaching takes focus.</p><p>Admin breaks it.</p><p className="muted">Youni keeps your calendar, payments, learner records, and messages together. No scattered tools. No duplicate work.</p><p>See what needs your attention, handle it, and get back to teaching.</p><p className="muted">Your time belongs with your learners.</p></div>
          <div className="orbit-card orbit-right"><CircleDollarSign size={16} /><span><strong>$1,240 collected</strong><small>Automatically</small></span></div>
        </section>

        <section className="feature-section" id="product">
          <div className="section-heading"><span className="eyebrow"><span className="eyebrow-icon gold"><CalendarDays size={14} /></span> Youni Schedule</span><h2>Every lesson, in the right place</h2><p>Share availability, book recurring lessons, send reminders, and manage changes without juggling calendars and messages.</p><a href={APP_URL}>Explore scheduling <ArrowRight size={15} /></a></div>
          <SchedulePreview />
          <div className="feature-points">
            <article><span><Sparkles size={17} /></span><h3>Shared availability</h3><p>Show learners the right times across every instructor and location.</p></article>
            <article><span><Clock3 size={17} /></span><h3>Automatic reminders</h3><p>Send confirmations and reminders without adding another task to your day.</p></article>
            <article><span><WandSparkles size={17} /></span><h3>Simple rescheduling</h3><p>Move a lesson and notify everyone in a few clicks.</p></article>
          </div>
        </section>

        <section className="feature-band" id="solutions">
          <div className="section-heading centered"><span className="eyebrow"><span className="eyebrow-icon green"><CreditCard size={14} /></span> Youni Billing</span><h2>Get paid without chasing</h2><p>Sell packages and memberships, send invoices, and collect payments from the same place you manage lessons.</p></div>
          <BillingPreview />
          <div className="band-footer"><div><strong>Automatic follow-ups</strong><span>Send invoices, payment reminders, and receipts on schedule.</span></div><div><strong>Clear payment status</strong><span>See what has been paid and what is still due.</span></div></div>
        </section>

        <section className="split-feature">
          <div className="split-copy"><span className="eyebrow"><span className="eyebrow-icon lilac"><Users size={14} /></span> Youni People</span><h2>Know every learner, not just their next appointment</h2><p>Keep goals, notes, attendance, billing, and parent messages on one learner record.</p><ul><li><Check size={15} /> A complete learner timeline</li><li><Check size={15} /> Clear progress notes for parents</li><li><Check size={15} /> The same context for every instructor</li></ul><a className="text-link" href={APP_URL}>Get started with Youni <ArrowRight size={15} /></a></div>
          <StudentPreview />
        </section>

        <section className="workflow-section">
          <div className="section-heading centered"><span className="eyebrow"><span className="eyebrow-icon blue"><Sparkles size={14} /></span> One connected workspace</span><h2>From first inquiry to every milestone</h2><p>Keep the learner, lesson, payment, and conversation on the same record.</p></div>
          <div className="workflow-grid">
            <article><span className="step-number">01</span><div className="flow-icon"><MessageSquare /></div><h3>A learner gets in touch</h3><p>Record their goals, preferences, and availability.</p></article>
            <article><span className="step-number">02</span><div className="flow-icon"><CalendarDays /></div><h3>Assign the right lesson</h3><p>Choose an instructor, service, and recurring time.</p></article>
            <article><span className="step-number">03</span><div className="flow-icon"><CreditCard /></div><h3>Keep one clear record</h3><p>Lessons, payments, notes, and messages stay together.</p></article>
          </div>
        </section>

        <section className="scale-section"><div className="scale-heading"><span>For solo tutors and growing teams</span><h2>Start with one learner. Add instructors and locations when you need them.</h2><p>Your schedule, records, and payments stay in the same workspace as your business grows.</p></div><div className="scale-stats"><div><strong>1</strong><span>workspace for your entire team</span></div><div><strong>Auto</strong><span>reminders, invoices, and receipts</span></div><div><strong>Live</strong><span>schedule and payment status</span></div></div></section>

        <section className="final-cta grid-surface" id="pricing"><span className="cta-mark"><LogoGlyph large /></span><h2>Let teachers teach.</h2><p>Manage the business side of teaching without leaving Youni.</p><a className="button button-dark" href={APP_URL}>Start using Youni <ArrowRight size={16} /></a><small>Open Youni and get to work.</small></section>
      </main>

      <footer className="site-footer">
        <div className="footer-top"><BrandMark /><p>Scheduling, payments, and learner management for teaching businesses.</p><a className="button button-light" href={APP_URL}>Open Youni</a></div>
        <div className="footer-links"><div><strong>Product</strong><a href="#product">Scheduling</a><a href="#solutions">Billing</a><a href="#solutions">Learner CRM</a></div><div><strong>Solutions</strong><a href="#solutions">Independent tutors</a><a href="#solutions">Schools & academies</a><a href="#solutions">Multi-location teams</a></div><div><strong>Company</strong><a href="#why-youni">Why Youni</a><a href="mailto:hello@joinyouni.com">Contact</a><a href={APP_URL}>Log in</a></div></div>
        <div className="footer-bottom"><span>© {new Date().getFullYear()} Youni, Inc.</span><div><a href="#top">Privacy</a><a href="#top">Terms</a></div></div>
      </footer>
    </div>
  )
}

export default App
